import { clients } from 'lib/https';
import { ConfigurationSettings } from '/models/configuration-settings';
import { PurchaseChannel } from 'constant/plan';

export interface SubscriptionPrice {
  itemId: string;
  unitPrice: number;
  discountPercent: number;
  taxPercent: number;
  paymentInterval: number;
  paymentIntervalUnit: number;
  actualPrice: number;
  discountedPrice: number;
  discountAmount: number;
  taxAmount: number;
}

export interface SubscriptionItem {
  itemId: string;
  price: SubscriptionPrice;
  planPrice: number;
  subTotalPrice: number;
  totalTax: number;
  totalPrice: number;
  totalDiscount: number;
  title: string;
  description: string;
  baseItem: boolean;
  active: boolean;
  externalId: string | null;
}

export interface SubscriptionFeature {
  key: string;
  active: boolean;
  value: string;
  translationKey: string;
}

export interface SubscriptionPlan {
  status: string | null;
  startDate: string;
  endDate: string;
  validUntil: string | null;
  itemId: string;
  items: SubscriptionItem[];
  planPrice: number;
  name: string;
  title: string;
  description: string;
  order: number;
  externalId: string | null;
  active: boolean;
  free: boolean;
  business: boolean;
  defaultPlan: boolean;
  trialPeriodDays: number;
  features: SubscriptionFeature[];
  currency: string;
  purchaseChannel: PurchaseChannel;
}

export interface GetPlansResponse {
  data: SubscriptionPlan[];
  errors: unknown;
}

export interface GetPlanResponse {
  data: SubscriptionPlan;
  errors: unknown;
}

export interface GetPlansFilter {
  name?: string;
  title?: string;
}

export interface GetPlansSort {
  property: string;
  isDescending: boolean;
}

export interface GetPlansRequest {
  page: number;
  pageSize: number;
  filter?: GetPlansFilter;
  sort?: GetPlansSort;
}

/**
 * Fetches all available subscription plans with pagination, filtering, and sorting.
 *
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @param {GetPlansRequest} request - The request payload containing pagination, filter, and sort options.
 * @returns {Promise<GetPlansResponse>} The response containing available subscription plans.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const plans = await getSubscriptionPlans(config, {
 *   page: 0,
 *   pageSize: 10,
 *   filter: { name: "BASIC", title: "Basic Plan" },
 *   sort: { property: "Order", isDescending: false }
 * });
 */
export const getSubscriptionPlans = async (
  config: ConfigurationSettings,
  request: GetPlansRequest
): Promise<GetPlansResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.getPlans}`;

  const res = await clients.post<GetPlansResponse>(
    url,
    config.blocksKey,
    JSON.stringify(request)
  );

  return res;
};

export interface BillingAddress {
  itemId: string;
  email: string;
  name: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  vat: string;
  organization: {
    itemId: string | null;
    name: string | null;
  } | null;
}

export interface BillingAddressRequest {
  email: string;
  name: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  vat: string | null;
}

export interface Organization {
  email: string;
  name: string;
  address: string;
}

export interface UpdateBillingAddressRequest {
  itemId: string;
  email: string;
  name: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  vat: string;
}

export interface UpdateBillingAddressResponse {
  itemId: string;
  errors: string | null;
  isSuccess: boolean;
}

export interface GenerateCheckoutLinkRequest {
  PlanId: string;
  PlanItemId: string;
  BillingAddress: BillingAddressRequest;
  Organization?: Organization;
  PromoCode?: string | null;
}

export interface GenerateCheckoutLinkResponse {
  link: string;
  errors?: string;
  isSuccess: boolean;
}

/**
 * Generates a checkout link for the subscription.
 *
 * @param {GenerateCheckoutLinkRequest} data - The checkout link generation data.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<GenerateCheckoutLinkResponse>} The response containing the checkout URL.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await generateCheckoutLink(checkoutData, config);
 */
export const generateCheckoutLink = async (
  data: GenerateCheckoutLinkRequest,
  config: ConfigurationSettings
): Promise<GenerateCheckoutLinkResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.generateCheckoutLink}`;

  return clients.post<GenerateCheckoutLinkResponse>(
    url,
    config.blocksKey,
    JSON.stringify({
      ...data,
      ProjectKey: config.blocksKey
    })
  );
};

export interface GetBillingAddressResponse {
  data: BillingAddress | null;
  errors: string | null;
}

export const getBillingAddress = async (
  config: ConfigurationSettings
): Promise<GetBillingAddressResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.getBillingAddress}`;

  return clients.get<GetBillingAddressResponse>(url, config.blocksKey);
};

/**
 * Updates the billing address for the user.
 *
 * @param {UpdateBillingAddressRequest} data - The billing address update data.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<UpdateBillingAddressResponse>} The response containing the update result.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await updateBillingAddress(updateData, config);
 */
export const updateBillingAddress = async (
  data: UpdateBillingAddressRequest,
  config: ConfigurationSettings
): Promise<UpdateBillingAddressResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.updateBillingAddress}`;

  return clients.post<UpdateBillingAddressResponse>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );
};

export const getCurrentPlan = async (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config: ConfigurationSettings
): Promise<GetPlanResponse | null> => {
  // In real implementation, this would make an API call
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.getCurrentPlan}`;
  return clients.get<GetPlanResponse>(url, config.blocksKey);
  // Mock subscription plan data (unused when returning null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  /* const mockPlan: SubscriptionPlan = {
    itemId: "c21a0fdf-53bc-4f74-b527-7186d60d1f18",
    items: [
      {
        itemId: "90964c34-11be-497f-9b5e-2162531ecc83",
        price: {
          itemId: "6489ce13-3d8d-4626-a8a6-f2ba52bc8a8a",
          unitPrice: 0.0,
          discountPercent: 0,
          taxPercent: 0,
          paymentInterval: 7,
          paymentIntervalUnit: 0,
          actualPrice: 0.0,
          discountedPrice: 0.0,
          discountAmount: 0.0,
          taxAmount: 0.0
        },
        planPrice: 0.0,
        subTotalPrice: 0.0,
        totalTax: 0.0,
        totalPrice: 0.0,
        totalDiscount: 0.0,
        title: "Free Trail",
        description: "Get Eagle GPT dashboard access",
        baseItem: true,
        active: true,
        externalId: null
      }
    ],
    planPrice: 0.0,
    name: "FREE",
    title: "Start Free with 7-Day Trial",
    description: "Slightly more bold, implies top-tier access.",
    order: 0,
    externalId: null,
    active: true,
    free: true,
    business: false,
    defaultPlan: true,
    trialPeriodDays: 7,
    features: [
      {
        key: "20 prompts per day",
        active: true
      },
      {
        key: "Limited GPT-4 access",
        active: true
      },
      {
        key: "No file uploads",
        active: true
      }
    ],
    currency: "CHF"
  };

  // Return null to simulate no subscription (for testing the prompt)
  // Change to mockPlan to simulate having a subscription
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(null); // Change this to mockPlan to test with subscription
    }, 1000);
  }); */
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SubscribeFreePlanRequest {
  // No request payload needed for free plan subscription
}

export interface SubscribeFreePlanResponse {
  isSuccess: boolean;
  itemId: string;
  errors: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface CancelSubscriptionRequest {
  // No request payload needed for cancellation
}

export interface CancelSubscriptionResponse {
  isSuccess: boolean;
  itemId: string;
  errors: string | null;
}

/**
 * Subscribes a user to the free plan.
 *
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<SubscribeFreePlanResponse>} The response containing the subscription result.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await subscribeFreePlan(config);
 */
export const subscribeFreePlan = async (
  config: ConfigurationSettings
): Promise<SubscribeFreePlanResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.subscribeFreePlan}`;

  return clients.post<SubscribeFreePlanResponse>(
    url,
    config.blocksKey,
    JSON.stringify({})
  );
};

/**
 * Cancels a user's subscription.
 *
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<CancelSubscriptionResponse>} The response containing the cancellation result.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await cancelSubscription(config);
 */
export const cancelSubscription = async (
  config: ConfigurationSettings
): Promise<CancelSubscriptionResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.cancelSubscription}`;

  return clients.post<CancelSubscriptionResponse>(
    url,
    config.blocksKey,
    JSON.stringify({})
  );
};

export interface UpdateSubscriptionRequest {
  PlanId: string;
  PlanItemId: string | undefined;
  Organization?: Organization;
}

export interface UpdateSubscriptionResponse {
  isSuccess: boolean;
  link: string;
  errors: string | null;
}

export interface UpgradeSubscriptionForUserRequest {
  userId: string;
  planId: string;
  planItemId: string;
}

export interface UpgradeSubscriptionForUserResponse {
  itemId: string;
  errors: {
    message: string;
  } | null;
  isSuccess: boolean;
}

export interface CancelAllUserSubscriptionsRequest {
  userId: string;
}

export interface CancelAllUserSubscriptionsResponse {
  itemId: string;
  errors: {
    message: string;
  } | null;
  isSuccess: boolean;
}

export interface GetCouponInfoRequest {
  promoCode: string;
  planId: string;
  planItemId: string;
}

export interface GetCouponInfoResponse {
  planPrice: number;
  totalDiscount: number;
  couponDiscount: number;
  subTotalPrice: number;
  totalTax: number;
  totalPrice: number;
  totalDiscountPercent: number;
  couponDiscountPercent: number;
  errors: {
    message: string;
  } | null;
  isSuccess: boolean;
}

export interface GetPlanUpdatePriceRequest {
  PlanId: string;
  PlanItemId: string;
}

export interface GetPlanUpdatePriceResponse {
  currentPlanRemainingAmount: number;
  newPlanRequiredAmount: number;
  subTotalPrice: number;
  totalTax: number;
  totalPrice: number;
  totalDiscount: number;
  totalDiscountPercent: number;
  planCycleStartDate: string;
  planCycleEndDate: string;
  errors: string | null;
  isSuccess: boolean;
}

/**
 * Updates the user's subscription to a new plan.
 *
 * @param {UpdateSubscriptionRequest} data - The update subscription data.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<UpdateSubscriptionResponse>} The response containing the update result.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await updateSubscription(updateData, config);
 */
export const updateSubscription = async (
  data: UpdateSubscriptionRequest,
  config: ConfigurationSettings
): Promise<UpdateSubscriptionResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.updateSubscription}`;

  return clients.post<UpdateSubscriptionResponse>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );
};

/**
 * Upgrades a user's subscription plan (admin function).
 *
 * @param {UpgradeSubscriptionForUserRequest} data - The upgrade subscription data containing userId, planId, and planItemId.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<UpgradeSubscriptionForUserResponse>} The response containing the upgrade result.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await upgradeSubscriptionForUser({
 *   userId: "f705bbda-82b1-4fce-bd3f-df7ef2a0c1ee",
 *   planId: "c8734548-1d22-4ba5-b4da-1161f1de6cd0",
 *   planItemId: "0fc02707-2006-45ed-98e8-e0079b291758"
 * }, config);
 */
export const upgradeSubscriptionForUser = async (
  data: UpgradeSubscriptionForUserRequest,
  config: ConfigurationSettings
): Promise<UpgradeSubscriptionForUserResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.upgradeSubscriptionPlanForUser}`;

  return clients.post<UpgradeSubscriptionForUserResponse>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );
};

/**
 * Cancels all subscriptions for a specific user (admin function).
 *
 * @param {CancelAllUserSubscriptionsRequest} data - The request data containing userId.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<CancelAllUserSubscriptionsResponse>} The response containing the cancellation result.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await cancelAllUserSubscriptions({
 *   userId: "f705bbda-82b1-4fce-bd3f-df7ef2a0c1ee"
 * }, config);
 */
export const cancelAllUserSubscriptions = async (
  data: CancelAllUserSubscriptionsRequest,
  config: ConfigurationSettings
): Promise<CancelAllUserSubscriptionsResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.cancelAllUserSubscriptions}`;

  return clients.post<CancelAllUserSubscriptionsResponse>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );
};

/**
 * Gets coupon information for a specific promo code and plan.
 *
 * @param {GetCouponInfoRequest} data - The coupon info request data containing promoCode and planId.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<GetCouponInfoResponse>} The response containing the coupon discount information.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await getCouponInfo({
 *   promoCode: "EAGLE2027",
 *   planId: "c8734548-1d22-4ba5-b4da-1161f1de6cd0"
 * }, config);
 */
export const getCouponInfo = async (
  data: GetCouponInfoRequest,
  config: ConfigurationSettings
): Promise<GetCouponInfoResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.getCouponInfo}`;

  return clients.post<GetCouponInfoResponse>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );
};

/**
 * Gets the updated price for a plan change/upgrade.
 *
 * @param {GetPlanUpdatePriceRequest} data - The plan update price request data containing PlanId and PlanItemId.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<GetPlanUpdatePriceResponse>} The response containing the updated price information.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await getPlanUpdatePrice({
 *   PlanId: "1f6a43a6-d2db-4343-9e2c-bceb9a5e1279",
 *   PlanItemId: "94cf0ef7-a5ee-4116-8fdd-b9e06ffeb503"
 * }, config);
 */
export const getPlanUpdatePrice = async (
  data: GetPlanUpdatePriceRequest,
  config: ConfigurationSettings
): Promise<GetPlanUpdatePriceResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.getPlanUpdatePrice}`;

  return clients.post<GetPlanUpdatePriceResponse>(
    url,
    config.blocksKey,
    JSON.stringify(data)
  );
};

/**
 * Fetches the current plan and updates the subscription store
 * This should be called once after login to initialize the subscription state
 */
export const initializeCurrentPlan = async (
  config: ConfigurationSettings,
  setCurrentPlan: (plan: SubscriptionPlan | null) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void
): Promise<void> => {
  try {
    setLoading(true);
    // Note: Don't call setError(null) here - it sets isLoading to false
    // Error is already cleared when setCurrentPlan succeeds (see store)
    const currentPlan = await getCurrentPlan(config);
    setCurrentPlan(currentPlan?.data ?? null);
  } catch (error) {
    setError(error instanceof Error ? error.message : 'Failed to fetch current plan');
    setCurrentPlan(null);
  } finally {
    setLoading(false);
  }
};

